/**
 * Helpers para construir URLs de share del micrositio publico: animales
 * (`getShareUrl`) y transmision en vivo (`getStreamingShareUrl`).
 *
 * Los dos toman la MISMA decision sobre el dominio — la de abajo — y por eso
 * viven juntos: el dia que cambie el criterio, cambia en un solo lugar.
 *
 * 2 caminos posibles segun config del productor:
 *
 *  1) Embed externo ON (productor con dominio propio: cabanapilaga, etc):
 *     URL → {url_embed_publico}?animal={id-slug}&evento={slug}
 *     → Branding del productor en la URL. Sin preview rica en WhatsApp
 *       porque no podemos inyectar og:tags en el sitio del productor.
 *
 *  2) Embed externo OFF (default PUJA):
 *     URL → {API_HOST}/share/animal/{evento_slug}/{id-slug}
 *     → Endpoint unfurl del backend Flask: sirve og:tags al crawler
 *       (WhatsApp/Telegram/etc) y redirige 302 al SPA cuando lo
 *       abre un humano.
 *
 * Decision UX (2026-06-18): cuando el productor activa embed externo,
 * priorizamos mantener SU dominio en la URL aunque eso implique perder la
 * preview rica. La consistencia de dominio genera confianza.
 *
 * Usado en:
 *  - pages/Micrositio/RematePage.jsx (handleCompartir)
 *  - pages/Micrositio/AnimalPage.jsx (handleCompartir)
 *  - pages/Micrositio/EnVivoPage.jsx (handleCompartirVivo)
 */

import { API_HOST } from './api';

function slugifyNombre(nombre) {
  if (!nombre) return '';
  return nombre
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getShareUrl({ config, animal, eventoSlug, fallback }) {
  const slug = slugifyNombre(animal?.nom_animal || '');
  const animalParam = slug
    ? `${animal.id_animal}-${slug}`
    : String(animal?.id_animal || '');

  // Embed externo: respetamos el dominio del productor (sin preview rica)
  if (config?.embed_externo) {
    const custom = (config.url_embed_publico || '').trim();
    if (custom) {
      const base = custom.replace(/\/$/, '');
      return `${base}?animal=${animalParam}&evento=${encodeURIComponent(
        eventoSlug || ''
      )}`;
    }
  }

  // Default PUJA: endpoint unfurl con og:tags + redirect
  if (eventoSlug && animal?.id_animal && API_HOST) {
    return `${API_HOST}/share/animal/${encodeURIComponent(
      eventoSlug
    )}/${animalParam}`;
  }

  // Fallback si faltan datos para construir el endpoint
  return (
    fallback || (typeof window !== 'undefined' ? window.location.href : '')
  );
}

/**
 * URL para compartir la TRANSMISIÓN EN VIVO del remate.
 *
 * Misma decisión que `getShareUrl` y por el mismo motivo: si el productor
 * declaró incrustación externa, el link se queda en SU dominio; si no, va a
 * PUJA. La consistencia de dominio genera confianza, y no tiene sentido que
 * compartir un animal respete el branding y compartir el vivo no.
 *
 * No pasa por el endpoint unfurl (`/share/...`) como los animales: ese existe
 * para servir og:tags de UN animal (foto, RP, categoría). Del vivo no hay
 * ficha que previsualizar, así que el link va derecho a la página.
 *
 * ⚠️ El `?vivo=1` sólo lo entiende el código de incrustación NUEVO. Si la
 * cabaña todavía tiene pegado el snippet viejo (que sólo lee `?animal=`), el
 * que abre el link cae en el catálogo del remate — con el banner "EN VIVO"
 * arriba, a un click. Degrada, no se rompe.
 */
export function getStreamingShareUrl({ config, eventoSlug, fallback }) {
  const slug = eventoSlug || '';

  if (config?.embed_externo) {
    const custom = (config.url_embed_publico || '').trim();
    if (custom) {
      const base = custom.replace(/\/$/, '');
      return `${base}?vivo=1&evento=${encodeURIComponent(slug)}`;
    }
  }

  if (slug && typeof window !== 'undefined') {
    return `${window.location.origin}/remate/${encodeURIComponent(slug)}/en-vivo`;
  }

  return (
    fallback || (typeof window !== 'undefined' ? window.location.href : '')
  );
}
